"use client";

import { c as _c } from "react/compiler-runtime";
import { useAtomValue } from "jotai";
import { PanelCornerDots } from "@/app/components/PanelCornerDots";
import type { CoursePageSection, FullCourseLevel } from "@repo/sanity-utils/query-types";
import type { CampusLocation } from "@repo/actions/AcademyApi/GetCampuses";
import { courseOfferingSelectionsAtom, learningFormatAtom, selectedCampusAtom } from "@/app/atoms/courseLevelSelections";
import { useAcademyUrl } from "@repo/test-server-url-resolver/hooks/useAcademyUrl";
import { useAopsUrl } from "@repo/test-server-url-resolver/hooks/useAopsUrl";
import { getActiveOffering, resolveSelectedOfferingKey } from "@/lib/activeCourseOffering";
import { isBeastAcademyModalitySlug } from "@/lib/beastAcademy";
import styles from "./CourseCTACard.module.css";
import { buildConsultationUrl, buildPlacementTestUrl } from "./courseUrl";
import { DisabledPrimaryCTA } from "./PrimaryCTA/DisabledPrimaryCTA";
import { PrimaryCTA } from "./PrimaryCTA/PrimaryCTA";
import { SecondaryCTA } from "./SecondaryCTA/SecondaryCTA";
interface CourseCTACardProps {
  campuses: CampusLocation[];
  campusCoursePrices: Readonly<Record<string, number>>;
  courseLevel: NonNullable<FullCourseLevel>;
  coursePageSection?: NonNullable<CoursePageSection>;
  initialPart?: string | null;
  showBookConsultationButtons: boolean;
}

/**
 * Select a course offering on the course page
 */
export function CourseCTACard(t0) {
  const $ = _c(53);
  const {
    campuses,
    campusCoursePrices,
    courseLevel,
    coursePageSection,
    initialPart: t1,
    showBookConsultationButtons
  } = t0;
  const initialPart = t1 === undefined ? null : t1;
  const {
    title,
    courseOfferings,
    modalityOfferings
  } = courseLevel;
  const {
    hasPlacementTest
  } = courseLevel as typeof courseLevel & {
    hasPlacementTest?: boolean;
  };
  const selectionsByCourse = useAtomValue(courseOfferingSelectionsAtom);
  const selectedModalityId = useAtomValue(learningFormatAtom);
  const selectedCampusId = useAtomValue(selectedCampusAtom);
  const academyUrl = useAcademyUrl();
  const aopsUrl = useAopsUrl();
  let t2;
  if ($[0] !== courseLevel || $[1] !== courseOfferings || $[2] !== initialPart || $[3] !== selectedModalityId || $[4] !== selectionsByCourse) {
    const selectedKey = resolveSelectedOfferingKey({
      courseId: courseLevel._id,
      courseOfferings,
      selectedModalityId,
      selectionsByCourse,
      initialPart
    });
    t2 = getActiveOffering({
      courseLevel,
      selectedModalityId,
      selectedOfferingKey: selectedKey
    });
    $[0] = courseLevel;
    $[1] = courseOfferings;
    $[2] = initialPart;
    $[3] = selectedModalityId;
    $[4] = selectionsByCourse;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const offering = t2;
  let campusIdForUrl;
  let isBeastAcademy;
  let priceDisplayMode;
  let selectedModalityOffering;
  let t3;
  if ($[6] !== academyUrl || $[7] !== modalityOfferings || $[8] !== offering || $[9] !== selectedCampusId || $[10] !== selectedModalityId) {
    selectedModalityOffering = modalityOfferings?.find(t4 => {
      const {
        modality
      } = t4;
      return modality?._id === selectedModalityId;
    });
    isBeastAcademy = isBeastAcademyModalitySlug(selectedModalityOffering?.modality?.slug?.current);
    priceDisplayMode = offering ? selectedModalityOffering?.modality?.price?.displayMode : undefined;
    const showLocationSelector = priceDisplayMode === "campusLocation";
    campusIdForUrl = showLocationSelector ? selectedCampusId : null;
    t3 = offering ? buildConsultationUrl(academyUrl, campusIdForUrl, selectedModalityOffering) : "";
    $[6] = academyUrl;
    $[7] = modalityOfferings;
    $[8] = offering;
    $[9] = selectedCampusId;
    $[10] = selectedModalityId;
    $[11] = campusIdForUrl;
    $[12] = isBeastAcademy;
    $[13] = priceDisplayMode;
    $[14] = selectedModalityOffering;
    $[15] = t3;
  } else {
    campusIdForUrl = $[11];
    isBeastAcademy = $[12];
    priceDisplayMode = $[13];
    selectedModalityOffering = $[14];
    t3 = $[15];
  }
  const consultationUrl = t3;
  let t4;
  if ($[16] !== aopsUrl) {
    t4 = buildPlacementTestUrl(aopsUrl);
    $[16] = aopsUrl;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  const placementTestUrl = t4;
  const courseSlug = courseLevel.slug.current;
  let t5;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <PanelCornerDots />;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  let t6;
  if ($[19] !== campusCoursePrices || $[20] !== campusIdForUrl || $[21] !== campuses || $[22] !== consultationUrl || $[23] !== courseLevel.inPersonBillingType || $[24] !== courseLevel.sequenceType || $[25] !== courseOfferings || $[26] !== coursePageSection || $[27] !== courseSlug || $[28] !== isBeastAcademy || $[29] !== offering || $[30] !== priceDisplayMode || $[31] !== selectedCampusId || $[32] !== selectedModalityOffering?.modality || $[33] !== title) {
    t6 = <div className={styles.contentTop}>{offering || isBeastAcademy ? <PrimaryCTA offering={offering} modality={selectedModalityOffering?.modality} courseOfferings={courseOfferings} courseTitle={title} courseSlug={courseSlug} coursePageSection={coursePageSection} campuses={campuses} campusCoursePrices={campusCoursePrices} campusIdForUrl={campusIdForUrl} consultationUrl={consultationUrl} priceDisplayMode={priceDisplayMode} selectedCampusId={selectedCampusId} sequenceType={courseLevel.sequenceType} inPersonBillingType={courseLevel.inPersonBillingType} /> : <DisabledPrimaryCTA coursePageSection={coursePageSection} />}</div>;
    $[19] = campusCoursePrices;
    $[20] = campusIdForUrl;
    $[21] = campuses;
    $[22] = consultationUrl;
    $[23] = courseLevel.inPersonBillingType;
    $[24] = courseLevel.sequenceType;
    $[25] = courseOfferings;
    $[26] = coursePageSection;
    $[27] = courseSlug;
    $[28] = isBeastAcademy;
    $[29] = offering;
    $[30] = priceDisplayMode;
    $[31] = selectedCampusId;
    $[32] = selectedModalityOffering?.modality;
    $[33] = title;
    $[34] = t6;
  } else {
    t6 = $[34];
  }
  let t7;
  if ($[35] !== campusIdForUrl || $[36] !== consultationUrl || $[37] !== courseLevel.sequenceType || $[38] !== courseOfferings || $[39] !== coursePageSection || $[40] !== courseSlug || $[41] !== hasPlacementTest || $[42] !== offering || $[43] !== placementTestUrl || $[44] !== selectedCampusId || $[45] !== showBookConsultationButtons) {
    t7 = offering && <SecondaryCTA courseOfferings={courseOfferings} offering={offering} courseSlug={courseSlug} coursePageSection={coursePageSection} campusIdForUrl={campusIdForUrl} selectedCampusId={selectedCampusId} consultationUrl={consultationUrl} hasPlacementTest={hasPlacementTest ?? false} placementTestUrl={placementTestUrl} showBookConsultationButtons={showBookConsultationButtons} sequenceType={courseLevel.sequenceType} />;
    $[35] = campusIdForUrl;
    $[36] = consultationUrl;
    $[37] = courseLevel.sequenceType;
    $[38] = courseOfferings;
    $[39] = coursePageSection;
    $[40] = courseSlug;
    $[41] = hasPlacementTest;
    $[42] = offering;
    $[43] = placementTestUrl;
    $[44] = selectedCampusId;
    $[45] = showBookConsultationButtons;
    $[46] = t7;
  } else {
    t7 = $[46];
  }
  let t8;
  if ($[47] !== t6 || $[48] !== t7) {
    t8 = <div className={styles.body}><div className={styles.content}>{t6}{t7}</div></div>;
    $[47] = t6;
    $[48] = t7;
    $[49] = t8;
  } else {
    t8 = $[49];
  }
  let t9;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <PanelCornerDots />;
    $[50] = t9;
  } else {
    t9 = $[50];
  }
  let t10;
  if ($[51] !== t8) {
    t10 = <div className={styles.card} id="cta">{t5}{t8}{t9}</div>;
    $[51] = t8;
    $[52] = t10;
  } else {
    t10 = $[52];
  }
  return t10;
}